// Service for managing user referrals

// Enhanced interfaces for two-way referral system
export interface ReferralRequest {
  id: string;
  candidateId: string;
  candidateName: string;
  candidateEmail: string;
  candidateTitle: string; // e.g., "Senior SRE Engineer"
  candidateExperience: string; // e.g., "5 years"
  candidateSkills: string[];
  candidateAvatar?: string;
  targetCompanyId: string;
  targetCompanyName: string;
  targetEmployeeId: string; // The employee being asked for referral
  message: string;
  status: 'pending' | 'accepted' | 'declined' | 'chat';
  createdAt: Date;
  updatedAt: Date;
  declineReason?: string;
  employeeNotes?: string;
  hasNewMessage?: boolean; // Indicates if there's an unread message from candidate
  lastMessageTime?: Date;
}

export interface EmployeeRating {
  employeeId: string;
  acceptanceRate: number;
  totalProcessed: number;
  avgResponseTime: number; // in hours
  toxicityScore: number; // 0-100, higher is worse
  consecutiveDeclines: number;
}

export interface CandidateRating {
  candidateId: string;
  referralSuccess: number; // percentage
  totalApplications: number;
  declineCount: number;
  qualityScore: number; // 0-100
}

// Original interfaces maintained for backward compatibility
export interface Referral {
  id: string;
  userId: string;
  companyId: string;
  companyName: string;
  submittedAt: Date;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  statusUpdatedAt: Date;
  notes?: string;
  referralCode?: string;
  expiresAt?: Date;
}

export interface ReferredPerson {
  id: string;
  referrerId: string;
  companyId: string;
  companyName: string;
  candidateName: string;
  candidateEmail: string;
  candidatePhone?: string;
  position: string;
  referredAt: Date;
  status: 'submitted' | 'screening' | 'interviewing' | 'hired' | 'rejected' | 'withdrawn';
  statusUpdatedAt: Date;
  notes?: string;
  referralBonus?: number;
  bonusPaid?: boolean;
}

export interface ReferralStats {
  totalReferrals: number;
  pendingReferrals: number;
  approvedReferrals: number;
  rejectedReferrals: number;
  recentReferrals: Referral[];
}

class ReferralsService {
  private baseUrl = '/api/referrals';
  private mockReferrals: Map<string, Referral[]> = new Map(); // Mock storage for development
  private mockReferredPeople: Map<string, ReferredPerson[]> = new Map(); // Mock storage for referred people
  private mockReferralRequests: Map<string, ReferralRequest[]> = new Map(); // Mock storage for referral requests
  private mockEmployeeRatings: Map<string, EmployeeRating> = new Map(); // Mock storage for employee ratings
  private mockCandidateRatings: Map<string, CandidateRating> = new Map(); // Mock storage for candidate ratings

  // Submit a referral request
  async submitReferral(
    userId: string, 
    companyId: string, 
    companyName: string,
    notes?: string
  ): Promise<Referral> {
    try {
      // For development, use mock implementation
      if (process.env.NODE_ENV === 'development' || !this.baseUrl.startsWith('http')) {
        const referral: Referral = {
          id: `ref_${Date.now()}`,
          userId,
          companyId,
          companyName,
          submittedAt: new Date(),
          status: 'pending',
          statusUpdatedAt: new Date(),
          notes
        };
        
        const userReferrals = this.mockReferrals.get(userId) || [];
        userReferrals.push(referral);
        this.mockReferrals.set(userId, userReferrals);
        
        // Store in localStorage for persistence
        localStorage.setItem(`referrals_${userId}`, JSON.stringify(userReferrals));
        
        return referral;
      }
      
      const response = await fetch(`${this.baseUrl}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify({
          userId,
          companyId,
          companyName,
          notes
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit referral');
      }

      return await response.json();
    } catch (error) {
      // console.error('Error submitting referral:', error);
      throw error;
    }
  }

  // Get user's referrals
  async getUserReferrals(
    userId: string, 
    filters?: {
      status?: string;
      companyId?: string;
      dateFrom?: Date;
      dateTo?: Date;
    }
  ): Promise<Referral[]> {
    try {
      // For development, use mock implementation
      if (process.env.NODE_ENV === 'development' || !this.baseUrl.startsWith('http')) {
        // Load from localStorage
        const stored = localStorage.getItem(`referrals_${userId}`);
        const referrals = stored ? JSON.parse(stored) : [];
        this.mockReferrals.set(userId, referrals);
        
        // Apply filters if provided
        let filteredReferrals = referrals;
        if (filters) {
          if (filters.status) {
            filteredReferrals = filteredReferrals.filter((ref: Referral) => ref.status === filters.status);
          }
          if (filters.companyId) {
            filteredReferrals = filteredReferrals.filter((ref: Referral) => ref.companyId === filters.companyId);
          }
          // Add date filtering if needed
        }
        
        return filteredReferrals;
      }
      
      const queryParams = new URLSearchParams({ userId });
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, value.toString());
          }
        });
      }

      const response = await fetch(`${this.baseUrl}?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch referrals');
      }

      return await response.json();
    } catch (error) {
      // console.error('Error fetching referrals:', error);
      throw error;
    }
  }

  // Cancel a referral
  async cancelReferral(userId: string, referralId: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/${referralId}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify({ userId })
      });

      if (!response.ok) {
        throw new Error('Failed to cancel referral');
      }
    } catch (error) {
      // console.error('Error canceling referral:', error);
      throw error;
    }
  }

  // Update referral notes
  async updateReferralNotes(
    userId: string, 
    referralId: string, 
    notes: string
  ): Promise<Referral> {
    try {
      const response = await fetch(`${this.baseUrl}/${referralId}/notes`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify({
          userId,
          notes
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update referral notes');
      }

      return await response.json();
    } catch (error) {
      // console.error('Error updating referral notes:', error);
      throw error;
    }
  }

  // Get referral statistics (legacy)
  async getReferralStatsLegacy(userId: string): Promise<ReferralStats> {
    try {
      const response = await fetch(`${this.baseUrl}/stats?userId=${userId}`, {
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch referral statistics');
      }

      return await response.json();
    } catch (error) {
      // console.error('Error fetching referral statistics:', error);
      throw error;
    }
  }

  // Check if user has already referred to a company
  async hasReferredToCompany(userId: string, companyId: string): Promise<boolean> {
    try {
      const response = await fetch(
        `${this.baseUrl}/check?userId=${userId}&companyId=${companyId}`,
        {
          headers: {
            'Authorization': `Bearer ${this.getAuthToken()}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to check referral status');
      }

      const result = await response.json();
      return result.hasReferred;
    } catch (error) {
      // console.error('Error checking referral status:', error);
      throw error;
    }
  }

  // Get companies user has referred to
  async getReferredCompanies(userId: string): Promise<Set<string>> {
    try {
      // For development, load from localStorage directly for efficiency
      if (process.env.NODE_ENV === 'development') {
        const stored = localStorage.getItem(`referrals_${userId}`);
        const referrals = stored ? JSON.parse(stored) : [];
        return new Set(referrals.map((r: Referral) => r.companyId));
      }
      
      const referrals = await this.getUserReferrals(userId);
      return new Set(referrals.map(r => r.companyId));
    } catch (error) {
      // console.error('Error getting referred companies:', error);
      return new Set();
    }
  }

  // Helper method to get auth token
  private getAuthToken(): string {
    // This should be implemented based on your auth system
    return localStorage.getItem('authToken') || '';
  }

  // Add a referred person
  async addReferredPerson(
    referrerId: string,
    personData: Omit<ReferredPerson, 'id' | 'referrerId' | 'referredAt' | 'statusUpdatedAt'>
  ): Promise<ReferredPerson> {
    try {
      // For development, use mock implementation
      if (process.env.NODE_ENV === 'development' || !this.baseUrl.startsWith('http')) {
        const referredPerson: ReferredPerson = {
          id: `rp_${Date.now()}`,
          referrerId,
          ...personData,
          referredAt: new Date(),
          statusUpdatedAt: new Date()
        };
        
        const userReferredPeople = this.mockReferredPeople.get(referrerId) || [];
        userReferredPeople.push(referredPerson);
        this.mockReferredPeople.set(referrerId, userReferredPeople);
        
        // Store in localStorage for persistence
        localStorage.setItem(`referredPeople_${referrerId}`, JSON.stringify(userReferredPeople));
        
        return referredPerson;
      }
      
      const response = await fetch(`${this.baseUrl}/people/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify({
          referrerId,
          ...personData
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add referred person');
      }

      return await response.json();
    } catch (error) {
      console.error('Error adding referred person:', error);
      throw error;
    }
  }

  // Get referred people by a user
  async getReferredPeople(
    referrerId: string,
    filters?: {
      status?: string;
      companyId?: string;
      dateFrom?: Date;
      dateTo?: Date;
    }
  ): Promise<ReferredPerson[]> {
    try {
      // For development, use mock implementation
      if (process.env.NODE_ENV === 'development' || !this.baseUrl.startsWith('http')) {
        // Load from localStorage
        const stored = localStorage.getItem(`referredPeople_${referrerId}`);
        const referredPeople = stored ? JSON.parse(stored) : [];
        this.mockReferredPeople.set(referrerId, referredPeople);
        
        // Apply filters if provided
        let filteredPeople = referredPeople;
        if (filters) {
          if (filters.status) {
            filteredPeople = filteredPeople.filter((person: ReferredPerson) => person.status === filters.status);
          }
          if (filters.companyId) {
            filteredPeople = filteredPeople.filter((person: ReferredPerson) => person.companyId === filters.companyId);
          }
        }
        
        return filteredPeople;
      }
      
      const queryParams = new URLSearchParams({ referrerId });
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, value.toString());
          }
        });
      }

      const response = await fetch(`${this.baseUrl}/people?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch referred people');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching referred people:', error);
      throw error;
    }
  }

  // Update referred person status
  async updateReferredPersonStatus(
    referrerId: string,
    personId: string,
    status: ReferredPerson['status'],
    notes?: string
  ): Promise<ReferredPerson> {
    try {
      // For development, use mock implementation
      if (process.env.NODE_ENV === 'development' || !this.baseUrl.startsWith('http')) {
        const stored = localStorage.getItem(`referredPeople_${referrerId}`);
        const referredPeople: ReferredPerson[] = stored ? JSON.parse(stored) : [];
        
        const personIndex = referredPeople.findIndex(p => p.id === personId);
        if (personIndex !== -1) {
          referredPeople[personIndex] = {
            ...referredPeople[personIndex],
            status,
            statusUpdatedAt: new Date(),
            notes: notes || referredPeople[personIndex].notes
          };
          
          localStorage.setItem(`referredPeople_${referrerId}`, JSON.stringify(referredPeople));
          return referredPeople[personIndex];
        }
        throw new Error('Referred person not found');
      }
      
      const response = await fetch(`${this.baseUrl}/people/${personId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify({
          referrerId,
          status,
          notes
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update referred person status');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating referred person status:', error);
      throw error;
    }
  }

  // Enhanced methods for two-way referral system
  async getReferralRequests(employeeId: string, companyId?: string): Promise<ReferralRequest[]> {
    try {
      // For development, use mock implementation
      if (process.env.NODE_ENV === 'development' || !this.baseUrl.startsWith('http')) {
        // Load from localStorage
        const stored = localStorage.getItem(`referralRequests_${employeeId}`);
        const requests = stored ? JSON.parse(stored) : [];
        this.mockReferralRequests.set(employeeId, requests);
        
        // Filter by company if provided
        let filteredRequests = requests;
        if (companyId) {
          filteredRequests = filteredRequests.filter((req: ReferralRequest) => req.targetCompanyId === companyId);
        }
        
        return filteredRequests.sort((a: ReferralRequest, b: ReferralRequest) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      }
      
      const queryParams = new URLSearchParams({ employeeId });
      if (companyId) queryParams.append('companyId', companyId);
      
      const response = await fetch(`${this.baseUrl}/requests?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch referral requests');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching referral requests:', error);
      return [];
    }
  }

  async getReferralStats(employeeId: string): Promise<{
    totalRequests: number;
    pending: number;
    accepted: number;
    declined: number;
  }> {
    const requests = await this.getReferralRequests(employeeId);
    
    return {
      totalRequests: requests.length,
      pending: requests.filter(r => r.status === 'pending').length,
      accepted: requests.filter(r => r.status === 'accepted').length,
      declined: requests.filter(r => r.status === 'declined').length
    };
  }

  async submitReferralRequest(request: Omit<ReferralRequest, 'id' | 'createdAt' | 'updatedAt'>): Promise<ReferralRequest> {
    const newRequest: ReferralRequest = {
      ...request,
      id: `ref_req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    try {
      // For development, use mock implementation
      if (process.env.NODE_ENV === 'development' || !this.baseUrl.startsWith('http')) {
        const targetRequests = this.mockReferralRequests.get(request.targetEmployeeId) || [];
        targetRequests.push(newRequest);
        this.mockReferralRequests.set(request.targetEmployeeId, targetRequests);
        
        // Store in localStorage for persistence
        localStorage.setItem(`referralRequests_${request.targetEmployeeId}`, JSON.stringify(targetRequests));
        
        return newRequest;
      }
      
      const response = await fetch(`${this.baseUrl}/requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify(newRequest)
      });

      if (!response.ok) {
        throw new Error('Failed to submit referral request');
      }

      return await response.json();
    } catch (error) {
      console.error('Error submitting referral request:', error);
      throw error;
    }
  }

  async updateReferralRequestStatus(
    requestId: string, 
    employeeId: string,
    status: 'accepted' | 'declined' | 'chat',
    reason?: string,
    notes?: string
  ): Promise<ReferralRequest | null> {
    try {
      // For development, use mock implementation
      if (process.env.NODE_ENV === 'development' || !this.baseUrl.startsWith('http')) {
        const stored = localStorage.getItem(`referralRequests_${employeeId}`);
        const requests: ReferralRequest[] = stored ? JSON.parse(stored) : [];
        const index = requests.findIndex(req => req.id === requestId);
        
        if (index !== -1) {
          requests[index] = {
            ...requests[index],
            status,
            declineReason: reason,
            employeeNotes: notes,
            updatedAt: new Date()
          };
          
          localStorage.setItem(`referralRequests_${employeeId}`, JSON.stringify(requests));
          
          // Update ratings
          await this.updateEmployeeRating(employeeId, status === 'accepted');
          await this.updateCandidateRating(requests[index].candidateId, status === 'accepted');
          
          return requests[index];
        }
        return null;
      }
      
      const response = await fetch(`${this.baseUrl}/requests/${requestId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify({ employeeId, status, declineReason: reason, employeeNotes: notes })
      });

      if (!response.ok) {
        throw new Error('Failed to update referral request status');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating referral request status:', error);
      return null;
    }
  }

  async updateEmployeeRating(employeeId: string, accepted: boolean): Promise<void> {
    let rating = this.mockEmployeeRatings.get(employeeId) || {
      employeeId,
      acceptanceRate: 0,
      totalProcessed: 0,
      avgResponseTime: 0,
      toxicityScore: 0,
      consecutiveDeclines: 0
    };
    
    rating.totalProcessed++;
    
    if (accepted) {
      rating.consecutiveDeclines = 0;
      rating.acceptanceRate = ((rating.acceptanceRate * (rating.totalProcessed - 1)) + 100) / rating.totalProcessed;
    } else {
      rating.consecutiveDeclines++;
      rating.acceptanceRate = (rating.acceptanceRate * (rating.totalProcessed - 1)) / rating.totalProcessed;
      
      // Update toxicity score based on consecutive declines
      if (rating.consecutiveDeclines >= 5) {
        rating.toxicityScore = Math.min(100, rating.toxicityScore + 20);
      }
    }
    
    this.mockEmployeeRatings.set(employeeId, rating);
    localStorage.setItem(`employeeRating_${employeeId}`, JSON.stringify(rating));
  }

  async updateCandidateRating(candidateId: string, accepted: boolean): Promise<void> {
    let rating = this.mockCandidateRatings.get(candidateId) || {
      candidateId,
      referralSuccess: 0,
      totalApplications: 0,
      declineCount: 0,
      qualityScore: 70 // Start with neutral score
    };
    
    rating.totalApplications++;
    
    if (accepted) {
      rating.referralSuccess = ((rating.referralSuccess * (rating.totalApplications - 1)) + 100) / rating.totalApplications;
      rating.qualityScore = Math.min(100, rating.qualityScore + 5);
    } else {
      rating.declineCount++;
      rating.referralSuccess = (rating.referralSuccess * (rating.totalApplications - 1)) / rating.totalApplications;
      
      // Decrease quality score if declined too many times
      if (rating.declineCount >= 5) {
        rating.qualityScore = Math.max(0, rating.qualityScore - 10);
      }
    }
    
    this.mockCandidateRatings.set(candidateId, rating);
    localStorage.setItem(`candidateRating_${candidateId}`, JSON.stringify(rating));
  }

  async getEmployeeRating(employeeId: string): Promise<EmployeeRating | null> {
    // Try to load from localStorage first
    const stored = localStorage.getItem(`employeeRating_${employeeId}`);
    if (stored) {
      const rating = JSON.parse(stored);
      this.mockEmployeeRatings.set(employeeId, rating);
      return rating;
    }
    return this.mockEmployeeRatings.get(employeeId) || null;
  }

  async getCandidateRating(candidateId: string): Promise<CandidateRating | null> {
    // Try to load from localStorage first
    const stored = localStorage.getItem(`candidateRating_${candidateId}`);
    if (stored) {
      const rating = JSON.parse(stored);
      this.mockCandidateRatings.set(candidateId, rating);
      return rating;
    }
    return this.mockCandidateRatings.get(candidateId) || null;
  }
}

export const referralsService = new ReferralsService();